import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMusic, FaDollarSign, FaPlay } from 'react-icons/fa';
import StreamPageHeader from './partial/stream/StreamPageHeader.jsx';
import LoadingSpinner from './partial/LoadingSpinner.jsx';
import MemoryClipPlayer from './partial/MemoryClipPlayer.jsx';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { fetchWithFallback, requestStatusLabel, tipAmount } from '../_helpers/listenerActivity.js';
import { useLocale } from '../contexts/LocaleContext.jsx';
import config from '../config.js';
import styles from '../styles/ListenerHistoryPage.module.css';

/** The two tabs, each named by its catalog key; `emptyKey` is the message when it has no rows. */
const TABS = [
  { id: 'requests', labelKey: 'history.tabs.requests', emptyKey: 'history.empty.requests', icon: FaMusic },
  { id: 'donations', labelKey: 'history.tabs.donations', emptyKey: 'history.empty.donations', icon: FaDollarSign },
];

/** Catalog keys for the backend's request statuses and tip methods; anything else is shown as served. */
const STATUS_KEYS = Object.freeze({
  played: 'history.status.played',
  // /user/requests' status_label for a request that was played.
  completed: 'history.status.played',
  playing: 'history.status.playing',
  removed: 'history.status.removed',
  queued: 'history.status.queued',
  pending: 'history.status.pending',
  skipped: 'history.status.skipped',
  rejected: 'history.status.rejected',
  cancelled: 'history.status.cancelled',
  denied: 'history.status.denied',
});
const METHOD_KEYS = Object.freeze({
  stripe: 'history.method.stripe',
  paypal: 'history.method.paypal',
  venmo: 'history.method.venmo',
  cashapp: 'history.method.cashapp',
  zelle: 'history.method.zelle',
});

/** `value` through `keys` (case- and space-insensitive) and `t`, or `value` itself when unknown. */
const enumLabel = (value, keys, t) => {
  const key = keys[String(value).toLowerCase().replace(/[\s_-]/g, '')];
  return key ? t(key) : value;
};

/** Shown in the date column when the row has no usable date. */
const NO_DATE = '—';

/** The row's date in the active locale ("Sep 15, 2026"); an em dash when the value is missing or unparseable. */
const formatDate = (value, format) => format.date(value, 'short') || NO_DATE;

/** A tip amount as US dollars in the active locale, always with cents so the ledger lines up ("$10.00"). */
const formatAmount = (amount, format) => format.usd(amount, { cents: 'always' });

/**
 * A request row, read from either the /user/requests shape (snake_case,
 * status_label) or the older camelCase one. A played request has a memory
 * (ENG-696), and a memory whose clip isn't known to be missing gets "Play my
 * song" (ENG-758), which opens the replay inline under the row.
 */
function RequestItem({ item, onGoPremium }) {
  const { t, format } = useLocale();
  const [playing, setPlaying] = useState(false);
  const title = item.title || t('common.fallbacks.unknownSong');
  const status = requestStatusLabel(item);
  const statusClass = status === 'completed' ? 'played' : status;
  const memoryId = item.memory_id ?? item.memoryId ?? null;
  const clipStatus = item.clip_status ?? item.clipStatus ?? null;
  const hasClip = memoryId != null && clipStatus !== 'unavailable';
  return (
    <div className={styles.historyItem}>
      <div className={styles.historyItemMain}>
        <span className={styles.historyItemTitle}>{title}</span>
        {item.artist && <span className={styles.historyItemSub}>{item.artist}</span>}
      </div>
      <div className={styles.historyItemMeta}>
        <span className={styles.historyItemBand}>{item.bandName || item.band_name || t('common.fallbacks.unknownBand')}</span>
        <span className={styles.historyItemDate}>{formatDate(item.requestedAt || item.requested_at, format)}</span>
        {status && (
          <span className={`${styles.historyItemStatus} ${styles[`status_${statusClass}`] || ''}`}>
            {enumLabel(status, STATUS_KEYS, t)}
          </span>
        )}
        {hasClip && (
          <button
            type="button"
            className={styles.clipButton}
            onClick={() => setPlaying((open) => !open)}
            aria-expanded={playing}
            aria-label={playing ? t('history.clip.hide') : t('history.clip.playLabel', { title })}
            data-testid={`history-clip-${memoryId}`}
          >
            {!playing && <FaPlay aria-hidden="true" />}
            {playing ? t('history.clip.hide') : t('history.clip.play')}
          </button>
        )}
      </div>
      {hasClip && playing && (
        <div className={styles.historyItemClip}>
          <MemoryClipPlayer memoryId={memoryId} title={title} onGoPremium={onGoPremium} />
        </div>
      )}
    </div>
  );
}

/** A tip row, from the /user/tips shape (amount_cents, provider) or the older one (amount, method). */
function DonationItem({ item }) {
  const { t, format } = useLocale();
  const amount = tipAmount(item);
  const method = item.method || item.provider;
  return (
    <div className={styles.historyItem}>
      <div className={styles.historyItemMain}>
        <span className={styles.historyItemTitle}>{formatAmount(amount, format)}</span>
        {method && <span className={styles.historyItemSub}>{enumLabel(method, METHOD_KEYS, t)}</span>}
      </div>
      <div className={styles.historyItemMeta}>
        <span className={styles.historyItemBand}>{item.bandName || item.band_name || t('common.fallbacks.unknownBand')}</span>
        <span className={styles.historyItemDate}>{formatDate(item.createdAt || item.created_at, format)}</span>
      </div>
    </div>
  );
}

function ListenerHistoryPage() {
  const navigate = useNavigate();
  const fetchWrapperRef = useRef(useFetchWrapper());
  const { t } = useLocale();

  const [activeTab, setActiveTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  // Whether both endpoints failed; the message itself is rendered from the catalog.
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      setError(false);

      const [requestsResult, donationsResult] = await Promise.allSettled([
        fetchWithFallback(fetchWrapperRef.current, `${config.apiBaseUrl}/user/requests`, 'requests'),
        fetchWithFallback(fetchWrapperRef.current, `${config.apiBaseUrl}/user/tips`, 'tips'),
      ]);

      setRequests(requestsResult.status === 'fulfilled' ? requestsResult.value : []);
      setDonations(donationsResult.status === 'fulfilled' ? donationsResult.value : []);

      if (requestsResult.status === 'rejected' && donationsResult.status === 'rejected') {
        setError(true);
        console.error('Failed to load listener history:', requestsResult.reason);
      } else {
        if (requestsResult.status === 'rejected') console.error('Failed to load requests:', requestsResult.reason);
        if (donationsResult.status === 'rejected') console.error('Failed to load donations:', donationsResult.reason);
      }

      setLoading(false);
    };

    fetchHistory();
  }, []);

  const activeItems = activeTab === 'requests' ? requests : donations;
  const activeTabDef = TABS.find((tab) => tab.id === activeTab) ?? TABS[0];

  return (
    <div className="home-page">
      <div className="home-page-wrapper">
        <StreamPageHeader />
        <div className={styles.page}>
          <header className={styles.header}>
            <h2 className={styles.title}>{t('history.title')}</h2>
            <p className={styles.subtitle}>{t('history.subtitle')}</p>
          </header>

          <div className={styles.tabs} role="tablist">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  id={`tab-${tab.id}`}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`panel-${tab.id}`}
                  className={`${styles.tab}${activeTab === tab.id ? ` ${styles.tabActive}` : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon aria-hidden="true" />
                  {t(tab.labelKey)}
                </button>
              );
            })}
          </div>

          <div
            id={`panel-${activeTab}`}
            role="tabpanel"
            aria-labelledby={`tab-${activeTab}`}
            tabIndex={0}
          >
          {loading ? (
            <div className={styles.stateContainer}>
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className={styles.stateContainer}>
              <p className={styles.errorText} role="alert">{t('history.loadError')}</p>
              <button
                className={styles.actionButton}
                onClick={() => window.location.reload()}
              >
                {t('common.actions.tryAgain')}
              </button>
            </div>
          ) : activeItems.length === 0 ? (
            <div className={styles.stateContainer}>
              <p className={styles.emptyText}>{t(activeTabDef.emptyKey)}</p>
              <button
                className={styles.actionButton}
                onClick={() => navigate('/performers')}
              >
                {t('common.actions.findPerformers')}
              </button>
            </div>
          ) : (
            <div className={styles.historyList}>
              {activeItems.map((item, index) =>
                activeTab === 'requests' ? (
                  // The subscription page is ENG-756; until it ships, Premium is on the profile.
                  <RequestItem key={item.id ?? index} item={item} onGoPremium={() => navigate('/upgrade?plan=listener_premium')} />
                ) : (
                  <DonationItem key={item.id ?? index} item={item} />
                )
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ListenerHistoryPage;
